import {Building2, History, MapPin, Percent, User, Users} from "lucide-react";
import type {AdminOrderDetail} from "@corporatesclub/shared";
import {Button} from "@/components/ui/button";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import {AdminBadge} from "../ui/AdminBadge";
import {OrderField, OrderFieldGrid, OrderSection, OrderStat} from "./OrderSection";

type Props = {
  detail: AdminOrderDetail;
  onRemoveDiscount: (discountId: number, orderId: number) => void;
  onOpenOrder?: (id: number) => void;
};

export function OrderClientTab({detail, onRemoveDiscount, onOpenOrder}: Props) {
  const profile = detail.clientProfile;

  return (
    <div className="space-y-4">
      <OrderSection
        title="Order contact"
        icon={<User />}
        action={detail.guest ? <AdminBadge variant="outline">Guest</AdminBadge> : null}
      >
        <OrderFieldGrid>
          <OrderField label="Name">{detail.client.nume}</OrderField>
          <OrderField label="Email">{detail.client.email || "—"}</OrderField>
          <OrderField label="Phone">{detail.client.telefon || "—"}</OrderField>
          {detail.client.tip === 1 ? (
            <>
              <OrderField label="Company">{detail.client.firmaNume || "—"}</OrderField>
              <OrderField label="CUI">{detail.client.firmaCui || "—"}</OrderField>
              {detail.client.firmaJ ? <OrderField label="Reg. no.">{detail.client.firmaJ}</OrderField> : null}
              {detail.client.firmaSediu ? <OrderField label="HQ" span={2}>{detail.client.firmaSediu}</OrderField> : null}
            </>
          ) : null}
        </OrderFieldGrid>
      </OrderSection>

      {profile ? (
        <>
          <OrderSection title="Account profile" icon={<Users />}>
            <OrderFieldGrid>
              <OrderField label="Client ID">#{profile.id}</OrderField>
              <OrderField label="Registered">{profile.registeredLabel || "—"}</OrderField>
              <OrderField label="Invite code">
                <span className="font-mono text-[13px]">{profile.cod || "—"}</span>
              </OrderField>
              <OrderField label="Group">{profile.grup || "—"}</OrderField>
              <OrderField label="Discount">{profile.discount || "0"}%</OrderField>
              <OrderField label="Commission">{profile.procent || "0"}%</OrderField>
              {profile.firma ? <OrderField label="Company name" span={2}>{profile.firma}</OrderField> : null}
            </OrderFieldGrid>
          </OrderSection>

          <OrderSection title="Referred by" icon={<User />}>
            {profile.referredBy ? (
              <OrderFieldGrid>
                <OrderField label="Name">{profile.referredBy.nume}</OrderField>
                <OrderField label="Email">{profile.referredBy.email}</OrderField>
                <OrderField label="Client ID">#{profile.referredBy.id}</OrderField>
                {profile.referredBy.telefon ? (
                  <OrderField label="Phone">{profile.referredBy.telefon}</OrderField>
                ) : null}
              </OrderFieldGrid>
            ) : (
              <p className="text-sm text-muted-foreground">Independent account (no referrer).</p>
            )}
          </OrderSection>

          <div className="grid gap-3 sm:grid-cols-2">
            <OrderStat label="Orders (excl. cancelled)" value={profile.ordersCount} />
            <OrderStat label="Total spent" value={`${profile.totalSpent.toFixed(2)} lei`} />
          </div>

          {profile.addresses.length ? (
            <OrderSection title="Saved addresses" icon={<MapPin />} dense>
              <ul className="space-y-2">
                {profile.addresses.map((a) => (
                  <li key={a.id} className="rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5 text-sm">
                    <p className="font-medium">{a.nume || `Address #${a.id}`}</p>
                    <p className="mt-0.5 text-muted-foreground">
                      {[a.adresa, a.localitate, a.judetNume, a.codPostal].filter(Boolean).join(", ")}
                    </p>
                  </li>
                ))}
              </ul>
            </OrderSection>
          ) : null}

          {profile.companies.length ? (
            <OrderSection title="Companies" icon={<Building2 />} dense>
              <ul className="space-y-2">
                {profile.companies.map((c) => (
                  <li key={c.id} className="rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5 text-sm">
                    <p className="font-medium">{c.firmaNume}</p>
                    <p className="mt-0.5 text-muted-foreground">
                      CUI {c.firmaCui || "—"}{c.firmaJ ? ` · ${c.firmaJ}` : ""}
                    </p>
                    {c.firmaSediu ? <p className="text-muted-foreground">{c.firmaSediu}</p> : null}
                  </li>
                ))}
              </ul>
            </OrderSection>
          ) : null}

          <OrderSection title="Order history" icon={<History />} dense>
            {profile.orders.length ? (
              <div className="-mx-1 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-9">ID</TableHead>
                      <TableHead className="h-9">Status</TableHead>
                      <TableHead className="h-9">Date</TableHead>
                      <TableHead className="h-9 text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profile.orders.map((o) => (
                      <TableRow
                        key={o.id}
                        className={onOpenOrder && o.id !== detail.id ? "cursor-pointer" : undefined}
                        onClick={() => { if(onOpenOrder && o.id !== detail.id) onOpenOrder(o.id); }}
                      >
                        <TableCell className="font-medium tabular-nums">
                          #{o.id}{o.id === detail.id ? " · current" : ""}
                        </TableCell>
                        <TableCell>
                          <AdminBadge status={o.status}>{o.statusLabel}</AdminBadge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{o.dateLabel}</TableCell>
                        <TableCell className="text-right tabular-nums">{o.total.toFixed(2)} lei</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No orders found.</p>
            )}
          </OrderSection>
        </>
      ) : (
        <OrderSection title="Account profile" icon={<Users />}>
          <p className="text-sm text-muted-foreground">Guest checkout — no registered client profile.</p>
        </OrderSection>
      )}

      {detail.discounts?.length ? (
        <OrderSection title="Discounts on this order" icon={<Percent />} dense>
          <ul className="space-y-2">
            {detail.discounts.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border/50 px-3 py-2.5 text-sm"
              >
                <span>
                  {d.nume} <span className="text-muted-foreground">({d.label})</span>
                </span>
                <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => onRemoveDiscount(d.id, detail.id)}>
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        </OrderSection>
      ) : null}
    </div>
  );
}
